import type { FormDataOf } from '../../../../../types/templates';
import type { DynamicOptions } from '../../../../../types/client-handler';
import { Player, Team } from '../../../../../types/sport';
import { FLAGS } from '../../../../../constants/flags';
import { disneyEspnPlayerVSPlayer } from '..';
import { PLAYER_VS_PLAYER_LEAGUES } from '../leagues';

type FormData = FormDataOf<typeof disneyEspnPlayerVSPlayer>;

// Helper interno: Obtiene equipos de una liga
const getTeamsByLeague = (leagueValue: string): Team[] => {
  const league = PLAYER_VS_PLAYER_LEAGUES.find(l => l.value === leagueValue);
  return league?.teams || [];
};

// Helper interno: Obtiene jugadores de un equipo
const getPlayersByTeam = (leagueValue: string, teamValue: string): Player[] => {
  const teams = getTeamsByLeague(leagueValue);
  const team = teams.find(t => t.value === teamValue);
  return team?.players || [];
};

// Helper interno: Formatea label con bandera
const formatPlayerLabel = (player: Player): string => {
  const flag = FLAGS[player.country] || '';
  return `${player.label} ${flag}`;
};

// Función principal exportada
export const getDynamicOptions = (
  fieldKey: string,
  formData: FormData,
  baseOptions?: DynamicOptions
): DynamicOptions | undefined => {
  const { league, team1, team2 } = formData;

  // Opciones de equipos (team1 y team2)
  if (fieldKey === 'team1' || fieldKey === 'team2') {
    if (!league) return baseOptions;

    const teams = getTeamsByLeague(league);
    let options = teams.map(t => ({
      value: t.value,
      label: t.label
    }));

    // Excluir equipo ya seleccionado
    if (fieldKey === 'team1' && team2) {
      options = options.filter(opt => opt.value !== team2);
    }
    if (fieldKey === 'team2' && team1) {
      options = options.filter(opt => opt.value !== team1);
    }

    return options;
  }

  // Opciones de jugadores para team1
  if (fieldKey === 'player1') {
    if (!league || !team1) return baseOptions;

    const players = getPlayersByTeam(league, team1);
    return players.map(player => ({
      value: player.value,
      label: formatPlayerLabel(player)
    }));
  }

  // Opciones de jugadores para team2
  if (fieldKey === 'player2') {
    if (!league || !team2) return baseOptions;

    const players = getPlayersByTeam(league, team2);
    return players.map(player => ({
      value: player.value,
      label: formatPlayerLabel(player)
    }));
  }

  return baseOptions;
};
